/**
 * @poc-to-production — Full-screen gate before Vera loads (Phase 2b).
 */
import { useState } from "preact/hooks";
import { getApsTier } from "../auth/apsConfig";
import { hasAlphaAccess } from "../featureFlags/alphaAccess";
import {
  ALPHA_SUPPORT_MESSAGE,
  getLaunchDarklyClientSideIdSuffix,
  isAlphaAccessGatingEnabled,
} from "../featureFlags/launchDarklyConfig";
import { PRODUCT_NAME } from "../productBrand";
import { useAuthStore } from "../stores/authStore";

export function LoginScreen() {
  const status = useAuthStore(s => s.status);
  const error = useAuthStore(s => s.error);
  const profile = useAuthStore(s => s.profile);
  const login = useAuthStore(s => s.login);
  const tier = getApsTier();
  const gating = isAlphaAccessGatingEnabled();

  const accessDenied = status === "access_denied";
  const [email, setEmail] = useState(profile?.email ?? "");
  const [emailCheckStatus, setEmailCheckStatus] = useState<
    "idle" | "checking" | "allowed" | "denied"
  >(accessDenied ? "denied" : "idle");
  const [emailCheckError, setEmailCheckError] = useState<string | null>(null);

  const showDeniedMessage = accessDenied || emailCheckStatus === "denied";
  const signInEnabled = !gating || emailCheckStatus === "allowed";

  function resetAccessDenied(): void {
    useAuthStore.setState({ status: "idle", error: null });
    setEmailCheckStatus("idle");
    setEmailCheckError(null);
  }

  async function onCheckEmail(): Promise<void> {
    if (accessDenied) resetAccessDenied();
    setEmailCheckError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailCheckError("Enter your Autodesk account email to continue.");
      return;
    }
    setEmailCheckStatus("checking");
    try {
      const allowed = await hasAlphaAccess(trimmed);
      setEmailCheckStatus(allowed ? "allowed" : "denied");
    } catch {
      setEmailCheckStatus("denied");
      setEmailCheckError(
        "Unable to verify access right now. Please try again in a moment."
      );
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--fc2-bg)",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          padding: "40px 32px",
          background: "var(--fc2-panel-bg)",
          border: "1px solid var(--fc2-border)",
          borderRadius: 8,
        }}
      >
        <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 600 }}>
          {PRODUCT_NAME}
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            color: "var(--fc2-text-dim)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {showDeniedMessage
            ? "Alpha access is limited to invited participants."
            : "AEC permitting & building-code compliance assistant. Sign in with your Autodesk account to continue."}
        </p>

        {showDeniedMessage ? (
          <p
            style={{
              color: "var(--fc2-text-dim)",
              fontSize: 13,
              marginBottom: 24,
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            {ALPHA_SUPPORT_MESSAGE}
          </p>
        ) : null}

        {gating ? (
          <div style={{ marginBottom: 20, textAlign: "left" }}>
            <label
              htmlFor="fc2-alpha-email"
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 500,
                marginBottom: 6,
                color: "var(--fc2-text-dim)",
              }}
            >
              Autodesk account email
            </label>
            <input
              id="fc2-alpha-email"
              type="email"
              autoComplete="email"
              value={email}
              disabled={emailCheckStatus === "checking"}
              onInput={e => setEmail((e.target as HTMLInputElement).value)}
              onKeyDown={e => {
                if (e.key === "Enter") void onCheckEmail();
              }}
              placeholder="you@company.com"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                fontSize: 14,
                borderRadius: 4,
                border: "1px solid var(--fc2-border)",
                background: "var(--fc2-bg)",
                color: "inherit",
                marginBottom: 10,
              }}
            />
            {emailCheckError ? (
              <p style={{ color: "#f48771", fontSize: 12, margin: "0 0 10px" }}>
                {emailCheckError}
              </p>
            ) : null}
            {emailCheckStatus !== "allowed" ? (
              <button
                type="button"
                disabled={emailCheckStatus === "checking"}
                onClick={() => void onCheckEmail()}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 500,
                  background: "transparent",
                  border: "1px solid var(--fc2-border)",
                  color: "inherit",
                  borderRadius: 4,
                  cursor: emailCheckStatus === "checking" ? "wait" : "pointer",
                }}
              >
                {emailCheckStatus === "checking"
                  ? "Checking access…"
                  : "Verify alpha access"}
              </button>
            ) : (
              <p
                style={{
                  fontSize: 12,
                  color: "#6ab04c",
                  margin: 0,
                  lineHeight: 1.4,
                }}
              >
                Access confirmed for {email.trim()}. You can sign in below.
              </p>
            )}
          </div>
        ) : null}

        {error && !showDeniedMessage ? (
          <p
            style={{
              color: "#f48771",
              fontSize: 12,
              marginBottom: 16,
              lineHeight: 1.4,
            }}
          >
            {error}
          </p>
        ) : null}

        {signInEnabled && emailCheckStatus === "allowed" ? (
          <button
            type="button"
            disabled={
              status === "redirecting" ||
              (gating && emailCheckStatus !== "allowed")
            }
            onClick={() => void login()}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: 14,
              fontWeight: 500,
              background: "var(--fc2-accent)",
              border: "none",
              color: "#fff",
              borderRadius: 4,
              cursor:
                status === "redirecting" ||
                (gating && emailCheckStatus !== "allowed")
                  ? "not-allowed"
                  : "pointer",
              opacity: gating && emailCheckStatus !== "allowed" ? 0.55 : 1,
            }}
          >
            {status === "redirecting"
              ? "Redirecting to Autodesk…"
              : "Sign in with Autodesk"}
          </button>
        ) : null}

        <p
          style={{
            marginTop: 20,
            fontSize: 11,
            color: "var(--fc2-text-dim)",
          }}
        >
          Environment: {tier.toUpperCase()}
          {gating
            ? ` · Alpha gating · LD …${getLaunchDarklyClientSideIdSuffix() ?? "????"}`
            : null}
        </p>
      </div>
    </div>
  );
}
